import type { Maybe, ProductInterface } from '@pylot-data/pylotschema'

/** Magento `sku` attribute max length. Product names are often longer or similar. */
const MAGENTO_SKU_MAX_LENGTH = 64

const SEARCH_TYPENAME = 'ProductSearchProduct'

/**
 * Live Search sometimes indexes the product name in `sku`.
 * Names typically include spaces or "+" (e.g. "Dispensador + Vaso + ...").
 */
export function isLikelyMagentoSku(sku: unknown): sku is string {
    if (typeof sku !== 'string' || sku.length === 0) return false
    if (sku.length > MAGENTO_SKU_MAX_LENGTH) return false
    if (/\s/.test(sku) || sku.includes('+')) return false
    return true
}

/**
 * Casaideas configurable parents include "C" in the SKU.
 * Must also look like a Magento SKU so product names with "C" are ignored.
 */
export function isSearchConfigurableSku(sku: unknown): sku is string {
    return isLikelyMagentoSku(sku) && sku.includes('C')
}

/**
 * El item sigue trayendo SOLO los campos de Live Search y se puede completar
 * desde Magento por SKU.
 *
 * `ProductSearchProduct` (el tipo que devuelve `productSearch` en el edge) tiene
 * seis campos: `name`, `sku`, `canonical_url`, `price_range`, `small_image` e
 * `isNew`. Todo lo demas que el tile necesita —`url_key`, `stock_status`,
 * `configurable_options`, `variants` y, desde EVO-263, `associated_products`—
 * llega por el enriquecimiento en lote.
 *
 * El `__typename` es la marca de "sin enriquecer": al completarse pasa a
 * `SimpleProduct` / `ConfigurableProduct` / `GiftCardProduct` y deja de ser
 * candidato, lo que evita volver a pedirlo.
 */
export function isSearchEnrichmentCandidate(
    product?: Maybe<ProductInterface>
): product is ProductInterface & { sku: string } {
    if (!product || !isLikelyMagentoSku(product.sku)) return false
    if (product.__typename !== SEARCH_TYPENAME) return false
    return !(product.name && product.sku === product.name)
}

/**
 * Ademas de estar sin enriquecer, el item parece un configurable de Casaideas.
 *
 * Esto NO decide que se enriquece —eso lo decide `isSearchEnrichmentCandidate`—
 * sino como se comporta el tile: con que precio se pinta y si el "agregar
 * rapido" tiene que desplegar las tallas en vez de ir a la PDP.
 */
export function isSearchConfigurableCandidate(
    product?: Maybe<ProductInterface>
): product is ProductInterface & { sku: string } {
    if (!isSearchEnrichmentCandidate(product)) return false
    if (!isSearchConfigurableSku(product.sku)) return false

    const options = (product as { configurable_options?: unknown[] })
        .configurable_options
    return !(Array.isArray(options) && options.length > 0)
}
